import { hasFunctionalConsent } from "./consent";
import {
  CANONICAL_LEAD_FIELDS,
  canonicalFieldForId,
  type CanonicalLeadField,
} from "./lead-fields";

export const DURABLE_TTL_DAYS = 14;

const STORAGE_KEY = "lead-profile-v2";
// v1 stored one savedAt for the whole record, so carrying a field forward on
// any write silently restarted every other field's clock. There is no honest
// way to backfill per-field timestamps for it, so it is purged, not migrated.
const LEGACY_STORAGE_KEY = "lead-profile-v1";
const TTL_MS = DURABLE_TTL_DAYS * 24 * 60 * 60 * 1000;

// Not a CanonicalLeadField — it must never enter the lead-hint cookie's
// positional bitmask — but the durable store persists it (spec A3).
const EXTRA_DURABLE_FIELDS = ["subscribecheckbox"] as const;

export type DurableLeadField =
  | CanonicalLeadField
  | (typeof EXTRA_DURABLE_FIELDS)[number];

type StoredField = { value: string; savedAt: number };

type Stored = {
  values: Partial<Record<DurableLeadField, StoredField>>;
};

type Profile = Partial<Record<DurableLeadField, string>>;

function isDurableField(key: string): key is DurableLeadField {
  return (
    (CANONICAL_LEAD_FIELDS as readonly string[]).includes(key) ||
    (EXTRA_DURABLE_FIELDS as readonly string[]).includes(key)
  );
}

function isStoredField(value: unknown): value is StoredField {
  return (
    typeof value === "object" &&
    value !== null &&
    typeof (value as StoredField).value === "string" &&
    typeof (value as StoredField).savedAt === "number"
  );
}

function pickCanonical(values: Profile): Profile {
  const out: Profile = {};
  for (const [key, value] of Object.entries(values)) {
    if (!isDurableField(key)) continue;
    const trimmed = value?.trim();
    if (trimmed) out[key] = trimmed;
  }
  return out;
}

function load(): Stored | undefined {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return undefined;
    const parsed: unknown = JSON.parse(raw);
    if (
      typeof parsed !== "object" ||
      parsed === null ||
      typeof (parsed as Stored).values !== "object" ||
      (parsed as Stored).values === null
    ) {
      return undefined;
    }
    return parsed as Stored;
  } catch {
    return undefined;
  }
}

function purgeLegacyProfile(): void {
  try {
    localStorage.removeItem(LEGACY_STORAGE_KEY);
  } catch {
    // Storage unavailable (Safari private mode, blocked cookies) — nothing to do.
  }
}

export function clearDurableProfile(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(LEGACY_STORAGE_KEY);
  } catch {
    // Storage unavailable (Safari private mode, blocked cookies) — nothing to do.
  }
}

function save(values: Stored["values"]): void {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ values } satisfies Stored),
    );
  } catch {
    // Quota or private mode — the feature degrades to today's behaviour.
  }
}

export function readDurableProfile(): Profile {
  if (typeof window === "undefined") return {};
  // A v1 record can't be trusted (single record-level savedAt), so it is
  // purged on sight rather than read, on every call, not just writes.
  purgeLegacyProfile();
  // Withdrawal deletes rather than hides, for the same reason expiry does:
  // hidden data still sits on the device, and would come back on a re-grant.
  if (!hasFunctionalConsent()) {
    clearDurableProfile();
    return {};
  }

  const stored = load();
  if (!stored) return {};

  // Each field expires on its own clock: carrying a field forward on a later
  // write must not extend a different field's life (the v1 bug this fixes).
  const now = Date.now();
  const survivors: Stored["values"] = {};
  let dropped = false;
  for (const [key, field] of Object.entries(stored.values)) {
    if (
      !isDurableField(key) ||
      !isStoredField(field) ||
      now - field.savedAt > TTL_MS
    ) {
      dropped = true;
      continue;
    }
    survivors[key as DurableLeadField] = field;
  }

  // Expiry evicts as well as hides: localStorage has no native TTL, so a
  // dropped field must be rewritten here, not just filtered out below, or
  // it never actually leaves the device.
  if (Object.keys(survivors).length === 0) {
    clearDurableProfile();
    return {};
  }
  if (dropped) save(survivors);

  const values: Profile = {};
  for (const [key, field] of Object.entries(survivors)) {
    values[key as DurableLeadField] = field.value;
  }
  return pickCanonical(values);
}

/**
 * The store lives on the visitor's device, so nothing evicts expired or
 * withdrawn-consent data unless a page runs this. Mounted site-wide so a
 * visitor who withdraws consent or never returns to a form still has the
 * data removed rather than merely hidden.
 */
export function enforceDurableRetention(): void {
  readDurableProfile();
}

/**
 * Mirrors a successful submission into the durable store, mapping each form's
 * own field ids onto the canonical vocabulary. Call only on success — the store
 * must hold what was actually sent, never what was typed.
 */
export function persistSubmittedLeadValues(
  values: Record<string, unknown>,
): void {
  const profile: Profile = {};
  for (const [id, value] of Object.entries(values)) {
    if (typeof value !== "string") continue;
    const canonical = canonicalFieldForId(id);
    if (canonical) profile[canonical] = value;
    else if (
      EXTRA_DURABLE_FIELDS.includes(id as (typeof EXTRA_DURABLE_FIELDS)[number])
    )
      profile[id as keyof Profile] = value;
  }
  writeDurableProfile(profile);
}

export function writeDurableProfile(values: Profile): void {
  if (typeof window === "undefined") return;
  purgeLegacyProfile();
  if (!hasFunctionalConsent()) return;

  const incoming = pickCanonical(values);
  if (Object.keys(incoming).length === 0) return;

  const now = Date.now();
  const existing = load();
  const merged: Stored["values"] = {};

  // Fields not present in this write keep their own existing savedAt — that is
  // the entire point of the change. An already-expired field is dropped here
  // rather than carried forward, so a later write can't resurrect it.
  for (const [key, field] of Object.entries(existing?.values ?? {})) {
    if (!isStoredField(field)) continue;
    if (now - field.savedAt > TTL_MS) continue;
    merged[key as DurableLeadField] = field;
  }

  for (const [key, value] of Object.entries(incoming)) {
    merged[key as DurableLeadField] = { value, savedAt: now };
  }

  save(merged);
}
